import { QueryKey, UseInfiniteQueryOptions, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  LeadsQuery,
  LeadsQueryVariables,
  PaginationOptions,
  SearchLeadsRequest,
} from "../../../generated/app-service-gql/graphql";
import { useGraphQlInfiniteQuery, useGraphQlQuery } from "../useGraphQlClient";

const LEADS_QUERY = graphql(`
  query leads($options: SearchLeadsRequest!, $pagination: PaginationOptions!) {
    leads(SearchLeadRequest: $options, PaginationOptions: $pagination) {
      results {
        id
        name
        description
        value
        createdAt
        updatedAt
        expiryDate
        assignedTo {
          guid
          firstName
          lastName
          active
        }
        createdBy {
          guid
          firstName
          lastName
          active
        }
        stage {
          id
          name
          color
          iconName
          iconColor
          transitionGroup
        }
        buyers {
          id
          name
          logoUrl
        }
        frameworks {
          id
          title
        }
        suppliers {
          id
          name
          logoUrl
        }
        customBuyers {
          id
          name
        }
      }
      total
      valueSum
    }
  }
`);

export type LeadFromSearch = LeadsQuery["leads"]["results"][number];

export function useLeads(
  request: SearchLeadsRequest,
  pagination: PaginationOptions,
  options?: UseQueryOptions<LeadsQuery, unknown, LeadsQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["leads", request, pagination],
    LEADS_QUERY,
    [{ options: request, pagination }],
    {
      staleTime: 1000 * 60 * 60, // 1 hour
      ...options,
    },
  );
  return { data: data?.leads, ...rest };
}

export function useInfiniteLeads(
  request: SearchLeadsRequest,
  pageSize: number = 10,
  options?: Pick<UseInfiniteQueryOptions<LeadsQuery>, "enabled">,
) {
  return useGraphQlInfiniteQuery<LeadsQuery, LeadsQueryVariables>(
    ["leads", request, pageSize],
    LEADS_QUERY,
    (page) => [{ options: request, pagination: { page, pageSize } }],
    {
      staleTime: 1000 * 60 * 60, // 1 hour
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.leads.total > allPages.length * pageSize ? allPages.length + 1 : undefined;
      },
      getPreviousPageParam: (_, allPages) => allPages.length - 1 || 1,
      ...options,
    },
  );
}
